<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import { dynamicTime, shortTimestamp } from 'shared/helpers/timeHelper';

const props = defineProps({
  conversation: { type: Object, required: true },
  stageOptions: { type: Array, required: true },
  stageAttributeKey: { type: String, required: true },
  inboxName: { type: String, default: '' },
  isMoving: { type: Boolean, default: false },
});

const emit = defineEmits(['open', 'stage-change', 'drag-start', 'drag-end']);
const { t } = useI18n();

const contact = computed(() => props.conversation.meta?.sender || {});
const contactName = computed(
  () => contact.value.name || t('CRM_PIPELINE.CARD.UNKNOWN_CONTACT')
);
const messagePreview = computed(() => {
  const message = props.conversation.last_non_activity_message || {};
  return (
    message.processed_message_content ||
    message.content ||
    t('CRM_PIPELINE.CARD.NO_MESSAGE')
  );
});
const ownerName = computed(() => {
  const meta = props.conversation.meta || {};
  return meta.assignee?.name || meta.team?.name || '';
});
const currentStage = computed(() => {
  const stageValue =
    props.conversation.custom_attributes?.[props.stageAttributeKey] || null;
  return (
    props.stageOptions.find(stage => stage.stageValue === stageValue)?.value ||
    props.stageOptions[0]?.value
  );
});
const lastActivity = computed(() => {
  const timestamp = Number(
    props.conversation.last_activity_at || props.conversation.timestamp
  );
  if (!timestamp) return '';
  return shortTimestamp(dynamicTime(timestamp), true);
});
const statusLabel = computed(() => {
  const key = props.conversation.status?.toUpperCase();
  return key ? t(`CRM_PIPELINE.STATUS.${key}`) : '';
});
const statusTone = computed(() => {
  const tones = {
    open: 'bg-kevoa-navy-100 text-kevoa-navy-700 dark:bg-kevoa-deep-control dark:text-kevoa-navy-200',
    pending:
      'bg-kevoa-warning-100 text-kevoa-ink-900 dark:bg-kevoa-deep-control dark:text-kevoa-neon-300',
    resolved:
      'bg-kevoa-success-100 text-kevoa-ink-900 dark:bg-kevoa-deep-control dark:text-kevoa-deep-body',
  };
  return (
    tones[props.conversation.status] ||
    'bg-kevoa-ink-100 text-kevoa-ink-500 dark:bg-kevoa-deep-control dark:text-kevoa-deep-body'
  );
});

const onStageChange = event => {
  emit('stage-change', {
    conversation: props.conversation,
    stageId: event.target.value,
  });
};

const onDragStart = event => {
  event.dataTransfer.effectAllowed = 'move';
  event.dataTransfer.setData('text/plain', String(props.conversation.id));
  emit('drag-start', props.conversation);
};
</script>

<template>
  <article
    :draggable="!isMoving"
    class="rounded-xl border border-kevoa-ink-200 bg-white shadow-sm transition hover:border-kevoa-navy-300 dark:border-kevoa-deep-border-subtle dark:bg-kevoa-deep-card dark:hover:border-kevoa-deep-border-strong"
    :class="{ 'opacity-60': isMoving }"
    @dragstart="onDragStart"
    @dragend="emit('drag-end')"
  >
    <button
      type="button"
      class="flex w-full flex-col gap-3 p-3 text-left"
      :aria-label="
        t('CRM_PIPELINE.CARD.OPEN_CONVERSATION', { name: contactName })
      "
      @click="emit('open', conversation)"
    >
      <span class="flex min-w-0 items-center gap-2">
        <Avatar
          :name="contactName"
          :src="contact.thumbnail || contact.avatar_url || ''"
          :size="32"
        />
        <span
          class="min-w-0 flex-1 truncate text-sm font-medium text-kevoa-navy-900 dark:text-white"
        >
          {{ contactName }}
        </span>
        <span
          class="shrink-0 text-xs text-kevoa-ink-500 dark:text-kevoa-deep-muted"
          >#{{ conversation.id }}</span
        >
      </span>

      <span
        class="line-clamp-2 min-h-10 text-xs text-kevoa-ink-700 dark:text-kevoa-deep-body"
      >
        {{ messagePreview }}
      </span>

      <span
        class="flex items-center justify-between gap-2 text-xs text-kevoa-ink-500 dark:text-kevoa-deep-muted"
      >
        <span class="truncate">{{
          inboxName || t('CRM_PIPELINE.CARD.UNKNOWN_INBOX')
        }}</span>
        <span v-if="lastActivity" class="shrink-0">{{ lastActivity }}</span>
      </span>

      <span class="flex items-center justify-between gap-2">
        <span
          class="truncate text-xs text-kevoa-ink-500 dark:text-kevoa-deep-muted"
        >
          {{ ownerName || t('CRM_PIPELINE.CARD.UNASSIGNED') }}
        </span>
        <span class="rounded-full px-2 py-0.5 text-xs" :class="statusTone">
          {{ statusLabel }}
        </span>
      </span>
    </button>

    <div
      class="border-t border-kevoa-ink-200 px-3 py-2 dark:border-kevoa-deep-border-subtle"
    >
      <label class="sr-only" :for="`crm-stage-${conversation.id}`">
        {{ t('CRM_PIPELINE.CARD.MOVE_STAGE', { name: contactName }) }}
      </label>
      <select
        :id="`crm-stage-${conversation.id}`"
        :value="currentStage"
        class="w-full rounded-md border border-kevoa-ink-300 bg-white px-2 py-1.5 text-xs text-kevoa-navy-900 outline-none focus:border-kevoa-navy-700 focus-visible:ring-2 focus-visible:ring-kevoa-neon-500 dark:border-kevoa-deep-border-strong dark:bg-kevoa-deep-control dark:text-white dark:focus:border-kevoa-neon-500"
        :disabled="isMoving"
        @click.stop
        @change="onStageChange"
      >
        <option
          v-for="stage in stageOptions"
          :key="stage.value"
          :value="stage.value"
        >
          {{ stage.label }}
        </option>
      </select>
    </div>
  </article>
</template>
